import {
    mdiFileTreeOutline,
    mdiGrid,
    mdiMagnify,
    mdiSwapVertical,
    mdiUnfoldMoreHorizontal,
    mdiViewAgendaOutline,
} from '@mdi/js';
import Icon from '@mdi/react';
import {
    Box,
    Button,
    ButtonProps,
    ClickAwayListener,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    SxProps,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import React, { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useBreakpoints from '../../../hooks/useBreakpoint';
import {
    ProjectDisplayType,
    ProjectViewType,
    setDisplayType,
    setFilter,
    setFilterVisible,
    setOpenNodes,
    setSortAz,
    setViewType,
} from '../../../redux/user/sliceUserProjects';
import { IAppReduxStore } from '../../../redux/store';
import { headingFontBold, vars, displayNoneStyle } from '../../../Theme';
import ViewControls from '../ViewControls';

/** Base button styles. */
const btnSx: SxProps = {
    textTransform: 'none',
    color: vars.font,
    ...headingFontBold,
    p: 1,
};

/** Styles for an active button. */
const btnSxActive: SxProps = {
    color: vars.brandGreen,

    '&:after': {
        backgroundColor: vars.brandGreen,
        position: 'absolute',
        borderRadius: 1,
        content: '""',
        height: 2,
        bottom: 4,
        right: 4,
        left: 4,
    },
};

/**
 * Creates an Icon component.
 * @param path - Icon SVG path.
 */
const icon = (path: string) => <Icon path={path} size={1} />;

/**
 * Gets the translation key for the controls' sorting button.
 * @param sortAz - Sorting direction.
 */
const getSortKey = (sortAz: boolean) => (sortAz ? 'sortAZ' : 'sortZA');

/**
 * Gets the translation key for the controls' expand/collapse button.
 * @param allNodesOpen - Flag that controls if all nodes are open or not.
 */
const getExpandKey = (allNodesOpen: boolean) => (allNodesOpen ? 'collapseAll' : 'expandAll');

/**
 * Component that allows the user to change how their projects are displayed.
 */
export const ProjectSelectionViewControls = () => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'projectSelectionView' });

    /** Determine screen size. */
    const { isDesktop } = useBreakpoints();

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Get relevant values from Redux store. */
    const filter = useSelector((store: IAppReduxStore) => store.userProjects.settings.filter);
    const displayType = useSelector(
        (store: IAppReduxStore) => store.userProjects.settings.displayType,
    );
    const viewType = useSelector((store: IAppReduxStore) => store.userProjects.settings.viewType);
    const sortAz = useSelector((store: IAppReduxStore) => store.userProjects.settings.sortAz);
    const isSearchVisible = useSelector(
        (store: IAppReduxStore) => store.userProjects.settings.filterVisible,
    );
    const openNodes = useSelector((store: IAppReduxStore) => store.userProjects.settings.openNodes);
    const projects = useSelector((store: IAppReduxStore) => store.userProjects.data);

    /** Determine if all nodes are already open. */
    const allNodesOpen = openNodes.length === projects.length;

    /**
     * Updates the active project view type.
     * @param type - New project view type.
     */
    const updateViewType = (type: ProjectViewType) => dispatch(setViewType(type));

    /**
     * Gets the props for a button that changes the active view type.
     * @param type - View type to create the button for.
     */
    const viewBtnProps = (type: ProjectViewType): ButtonProps => {
        let title: string;

        switch (type) {
            case ProjectViewType.All:
                title = t('projects_all');
                break;
            case ProjectViewType.Favourites:
                title = t('projects_favourite');
                break;
            /** Recently opened tab is currently not displayed therefore props are not used. */
            case ProjectViewType.Recent:
                title = t('projects_recent');
                break;
        }

        return {
            onClick: () => updateViewType(type),
            sx: { ...btnSx, ...(viewType === type ? btnSxActive : {}) },
            children: title,
        };
    };

    /**
     * Toggles the project sorting direction.
     */
    const toggleSort = () => dispatch(setSortAz(!sortAz));

    /**
     * Toggles the visibility of the filter input.
     * @param visible - New input visibility.
     */
    const toggleFilter = (visible: boolean) => dispatch(setFilterVisible(visible));

    /**
     * Gets the filter controls for the view's mobile version.
     */
    const getMobileFilter = () =>
        isSearchVisible || !!filter?.length ? (
            <ClickAwayListener onClickAway={() => toggleFilter(false)}>
                {filterProjectsField}
            </ClickAwayListener>
        ) : (
            <IconButton title={t('projectSearch')} onClick={() => toggleFilter(true)}>
                {icon(mdiMagnify)}
            </IconButton>
        );

    /** ToggleButtonGroup that allows the user to see and set the project display type. */
    const toggleButton = (
        <ToggleButtonGroup
            onChange={(_, value: ProjectDisplayType) =>
                value !== null && dispatch(setDisplayType(value))
            }
            sx={{ button: { borderWidth: +isDesktop, p: 0.75 } }}
            value={displayType}
            color='primary'
            exclusive
        >
            <ToggleButton value={ProjectDisplayType.Tree} title={t('treeView')}>
                {icon(mdiFileTreeOutline)}
            </ToggleButton>

            <ToggleButton value={ProjectDisplayType.Tile} title={t('tileView')}>
                {icon(mdiGrid)}
            </ToggleButton>

            <ToggleButton value={ProjectDisplayType.List} title={t('flatList')}>
                {icon(mdiViewAgendaOutline)}
            </ToggleButton>
        </ToggleButtonGroup>
    );

    /** Text input for filtering visible projects. */
    const filterProjectsField = (
        <TextField
            id='input-filter'
            value={filter}
            onBlur={() => dispatch(setFilterVisible(false))}
            onChange={e => dispatch(setFilter(e.target.value))}
            placeholder={t('projectSearch')}
            sx={{ input: { py: 1 } }}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position='start'>{icon(mdiMagnify)}</InputAdornment>
                    ),
                },
            }}
        />
    );

    /** Tablet/Mobile View. */
    if (!isDesktop) {
        return (
            <Stack
                direction='row'
                sx={{
                    alignItems: 'center',
                    px: 3,
                }}
            >
                <Select
                    variant='standard'
                    value={viewType}
                    onChange={e => updateViewType(e.target.value as ProjectViewType)}
                >
                    <MenuItem value={ProjectViewType.All}>{t('projects_all')}</MenuItem>

                    <MenuItem value={ProjectViewType.Favourites}>
                        {t('projects_favourite')}
                    </MenuItem>

                    <MenuItem sx={displayNoneStyle} value={ProjectViewType.Recent}>
                        {t('projects_recent')}
                    </MenuItem>
                </Select>
                <Box sx={{ display: 'flex', flexGrow: 4, justifyContent: 'center' }}>
                    {toggleButton}
                </Box>
                <Box>
                    <IconButton title={t(getSortKey(sortAz))} onClick={toggleSort}>
                        {icon(mdiSwapVertical)}
                    </IconButton>

                    {getMobileFilter()}
                </Box>
            </Stack>
        );
    }

    return (
        <ViewControls
            header={<Stack sx={{ ml: 1 }}>{t('projects')}</Stack>}
            row1={
                <>
                    {filterProjectsField}

                    <Box sx={{ ml: 1 }}>{toggleButton}</Box>
                </>
            }
            row2={
                <Stack
                    direction='row'
                    sx={{
                        alignItems: 'center',
                        p: 1,
                    }}
                >
                    <Stack sx={{ flexGrow: 4 }} direction='row' spacing={2}>
                        <Button {...viewBtnProps(ProjectViewType.All)} />

                        <Button {...viewBtnProps(ProjectViewType.Favourites)} />

                        <Button sx={displayNoneStyle} />
                    </Stack>

                    <Stack
                        direction='row'
                        sx={{
                            alignItems: 'center',
                            flexGrow: 0,
                        }}
                    >
                        {displayType === ProjectDisplayType.Tree && (
                            <Button
                                sx={{ ...btnSx }}
                                onClick={() => {
                                    dispatch(
                                        setOpenNodes(
                                            allNodesOpen ? [] : [...projects.map(x => x.category)],
                                        ),
                                    );
                                }}
                            >
                                {icon(mdiUnfoldMoreHorizontal)}
                                {t(getExpandKey(allNodesOpen))}
                            </Button>
                        )}

                        <Button sx={{ ...btnSx }} onClick={toggleSort}>
                            {icon(mdiSwapVertical)}
                            {t(sortAz ? 'sortAZ' : 'sortZA')}
                        </Button>
                    </Stack>
                </Stack>
            }
        />
    );
};
